import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export interface BookNotification {
  id: string;
  user_id: string;
  book_id: number;
  bundle_id?: number;
  created_at: string;
}

export function useBookNotifications() {
  const [notifications, setNotifications] = useState<BookNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('book_notifications')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading book notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  const isSubscribed = useCallback((bookId: number) => {
    return notifications.some(n => n.book_id === bookId);
  }, [notifications]);

  const subscribeToBook = useCallback(async (bookId: number, bundleId?: number) => {
    if (!user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in to get notified when this book is available.",
        variant: "destructive"
      });
      return false;
    }

    try {
      const { error } = await supabase
        .from('book_notifications')
        .insert({
          user_id: user.id,
          book_id: bookId,
          bundle_id: bundleId
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: "Already subscribed",
            description: "You're already subscribed to notifications for this book.",
          });
          return true;
        }
        throw error;
      }

      await loadNotifications();
      toast({
        title: "Notification set! 🔔",
        description: "We'll notify you when this book becomes available.",
      });
      return true;
    } catch (error) {
      console.error('Error subscribing to book:', error);
      toast({
        title: "Failed to set notification",
        description: "Please try again later.",
        variant: "destructive"
      });
      return false;
    }
  }, [user?.id, loadNotifications]);

  const unsubscribeFromBook = useCallback(async (bookId: number) => {
    if (!user?.id) return false;

    try {
      const { error } = await supabase
        .from('book_notifications')
        .delete()
        .eq('user_id', user.id)
        .eq('book_id', bookId);

      if (error) throw error;

      await loadNotifications();
      toast({
        title: "Notification removed",
        description: "You'll no longer be notified about this book.",
      });
      return true;
    } catch (error) {
      console.error('Error unsubscribing from book:', error);
      toast({
        title: "Failed to remove notification",
        description: "Please try again later.",
        variant: "destructive"
      });
      return false;
    }
  }, [user?.id, loadNotifications]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  return {
    notifications,
    isLoading,
    isSubscribed,
    subscribeToBook,
    unsubscribeFromBook,
    refreshNotifications: loadNotifications
  };
}